import React, { useState, useRef, useEffect } from 'react';
import './ReceiptUploadSheet.css';
import {
  uploadReceipt,
  saveReceiptAsShoppingList,
  getReceiptHistory,
  saveReceiptToHistory,
  unsaveReceipt,
  saveBetaFeedback,
  isAdminUser,
  saveAliasDirect,
} from '../utils/db';
import { db } from '../firebase';
import { storeIsSupported, getStoreName } from '../utils/storeConfig';
import { trackReceiptScanStart, trackReceiptScanComplete } from '../utils/analytics';

const SAVED_RECEIPT_LIMIT = 10;

// Allowed image MIME types and file size limit
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'];
const MAX_SIZE_MB     = 10;

// Processing timeout thresholds
const SLOW_THRESHOLD_MS    = 60_000;   // 60s → show "taking longer" message
const TIMEOUT_THRESHOLD_MS = 120_000;  // 120s → auto-fail

// Short date formatter for replace picker
const fmtShortDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ─────────────────────────────────────────────────────────────────────────────

function ReceiptCorrectionPanel({ item, search, results, loading, saving, onSearch, onSave, onCancel }) {
  const [selected,     setSelected]     = useState(null);
  const [saveAsAlias,  setSaveAsAlias]  = useState(true);

  return (
    <div className="receipt-correction-panel">
      <p className="receipt-correction-title">Correct match for "{item.productText}"</p>
      <input
        className="receipt-correction-search"
        type="text"
        placeholder="Search products…"
        value={search}
        onChange={e => { setSelected(null); onSearch(e.target.value); }}
        autoFocus
      />
      {loading && <p className="receipt-correction-loading">Searching…</p>}
      {results.length > 0 && !selected && (
        <div className="receipt-correction-results">
          {results.map(p => (
            <button
              key={p.id}
              className="receipt-correction-result-row"
              onClick={() => { setSelected(p); onSearch(p.name); }}
            >
              <span className="receipt-correction-result-name">{p.name}</span>
              <span className="receipt-correction-result-id">{p.id}</span>
            </button>
          ))}
        </div>
      )}
      {selected && (
        <div className="receipt-correction-selected">
          ✓ {selected.name}
        </div>
      )}
      <label className="receipt-correction-alias-check">
        <input
          type="checkbox"
          checked={saveAsAlias}
          onChange={e => setSaveAsAlias(e.target.checked)}
        />
        Remember "{item.productText}" as alias for future scans
      </label>
      <div className="receipt-correction-actions">
        <button className="receipt-correction-cancel" onClick={onCancel}>Cancel</button>
        <button
          className="receipt-correction-save"
          disabled={!selected || saving}
          onClick={() => onSave(item, selected, saveAsAlias)}
        >
          {saving ? 'Saving…' : 'Save correction'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ReceiptUploadSheet({ isOpen, onClose, onNavigate, user }) {
  const [phase,                   setPhase]                   = useState('idle');  // idle | uploading | processing | done | replace | error
  const [progress,                setProgress]                = useState(0);
  const [result,                  setResult]                  = useState(null);    // { itemCount, matchedCount, matchedItems, detectedStoreId, receiptId }
  const [errorMsg,                setErrorMsg]                = useState('');
  const [listSaving,              setListSaving]              = useState(false);
  const [listSaved,               setListSaved]               = useState(false);
  const [savedListId,             setSavedListId]             = useState(null);
  const [receiptSaved,            setReceiptSaved]            = useState(false);
  const [savingToPastScans,       setSavingToPastScans]       = useState(false);
  const [savedReceiptsForReplace, setSavedReceiptsForReplace] = useState([]);
  const [slowProcessing,          setSlowProcessing]          = useState(false);

  // ── Moment 1 — scan quality micro-prompt (one-time) ─────────────────────────
  const [scanPromptDone,   setScanPromptDone]   = useState(() => !!localStorage.getItem('cms_seen_scan_prompt'));
  const [scanAnswer,       setScanAnswer]       = useState(null);
  const [scanFollowUp,     setScanFollowUp]     = useState(false);  // show text after "Not really"
  const [scanFollowUpText, setScanFollowUpText] = useState('');

  // ── Moment 2 — post-list micro-prompt (one-time, 2-step) ────────────────────
  const [listPromptDone,   setListPromptDone]   = useState(() => !!localStorage.getItem('cms_seen_list_prompt'));
  const [listStep,         setListStep]         = useState(0);  // 0=clarity 1=value 2=cta
  const [listClarityAns,   setListClarityAns]   = useState(null);
  const [listFollowUp,     setListFollowUp]     = useState(false); // show text after "Confusing"
  const [listFollowUpText, setListFollowUpText] = useState('');

  const [correctingIdx,    setCorrectingIdx]    = useState(null);  // index into matchedItems being corrected
  const [correctionSearch, setCorrectionSearch] = useState('');
  const [correctionResults, setCorrectionResults] = useState([]);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionSaving,  setCorrectionSaving]  = useState(false);
  const [allProducts,       setAllProducts]       = useState(null); // lazy-loaded

  const fileInputRef        = useRef(null);  // camera
  const libraryInputRef     = useRef(null);  // photo library
  const uploadRef     = useRef(null);
  const slowTimerRef  = useRef(null);
  const hardTimerRef  = useRef(null);

  // Clear timeout timers
  const clearTimers = () => {
    clearTimeout(slowTimerRef.current);
    clearTimeout(hardTimerRef.current);
  };

  // Auto-close 1.5s after receipt is saved to Past Scans
  useEffect(() => {
    if (!receiptSaved) return;
    const timer = setTimeout(() => {
      uploadRef.current?.stopListening?.();
      onClose?.();
    }, 1500);
    return () => clearTimeout(timer);
  }, [receiptSaved, onClose]);

  // Reset state every time the sheet opens
  useEffect(() => {
    if (isOpen) {
      setPhase('idle');
      setProgress(0);
      setResult(null);
      setErrorMsg('');
      setSlowProcessing(false);
      setListSaving(false);
      setListSaved(false);
      setSavedListId(null);
      setReceiptSaved(false);
      setSavingToPastScans(false);
      setSavedReceiptsForReplace([]);
      setScanAnswer(null);
      setScanFollowUp(false);
      setScanFollowUpText('');
      setListStep(0);
      setListClarityAns(null);
      setListFollowUp(false);
      setListFollowUpText('');
      // scanPromptDone / listPromptDone deliberately NOT reset — backed by localStorage
      clearTimers();
    } else {
      uploadRef.current?.stopListening?.();
      clearTimers();
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) fileInputRef.current.value = '';

    if (!file) return;

    // Validate type
    if (!ACCEPTED_TYPES.includes(file.type.toLowerCase()) && !file.name.toLowerCase().match(/\.(jpg|jpeg|png|webp|heic)$/)) {
      setErrorMsg('Please choose a photo (JPEG, PNG, WEBP or HEIC).');
      setPhase('error');
      return;
    }

    // Validate size
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setErrorMsg(`Photo is too large. Please choose one under ${MAX_SIZE_MB} MB.`);
      setPhase('error');
      return;
    }

    setPhase('uploading');
    setProgress(0);
    setErrorMsg('');

    try {
      const handle = uploadReceipt(file, {
        onProgress: (pct) => {
          setProgress(pct);
          if (pct === 100) {
            setPhase('processing');
            setSlowProcessing(false);
            slowTimerRef.current = setTimeout(() => {
              setSlowProcessing(true);
            }, SLOW_THRESHOLD_MS);
            hardTimerRef.current = setTimeout(() => {
              setErrorMsg('This is taking too long. Please try again — it may be a temporary issue.');
              setPhase('error');
              handle.stopListening();
              clearTimers();
            }, TIMEOUT_THRESHOLD_MS);
          }
        },
        onStatusChange: (data, rid) => {
          if (data.status === 'complete') {
            clearTimers();
            setResult({
              itemCount:       data.itemCount        ?? 0,
              matchedCount:    data.matchedCount      ?? 0,
              reviewsCreated:  data.reviewsCreated    ?? 0,
              matchedItems:    data.matchedItems      ?? [],
              detectedStoreId: data.detectedStoreId   ?? null,
              receiptId:       rid,
            });
            trackReceiptScanComplete(data.matchedCount ?? 0, data.itemCount ?? 0);
            setPhase('done');
            handle.stopListening();
          } else if (data.status === 'failed') {
            clearTimers();
            setErrorMsg(data.failureReason || 'Processing failed. Please try again.');
            setPhase('error');
            handle.stopListening();
          }
        },
      });
      uploadRef.current = handle;
    } catch (err) {
      setErrorMsg(err.message || 'Could not start upload. Please try again.');
      setPhase('error');
    }
  };

  const triggerCamera = () => {
    trackReceiptScanStart('camera');
    fileInputRef.current?.click();
  };

  const triggerLibrary = () => {
    trackReceiptScanStart('library');
    libraryInputRef.current?.click();
  };

  const handleClose = () => {
    uploadRef.current?.stopListening?.();
    onClose?.();
  };

  // ── Save to Past Scans ──────────────────────────────────────────────────────
  const handleSaveToPastScans = async () => {
    if (!result?.receiptId || savingToPastScans) return;
    setSavingToPastScans(true);
    try {
      // Load saved receipts — gives us count AND list for replace flow
      const saved = await getReceiptHistory(SAVED_RECEIPT_LIMIT + 2);
      if (saved.length < SAVED_RECEIPT_LIMIT) {
        const { success } = await saveReceiptToHistory(result.receiptId);
        if (success) setReceiptSaved(true);
      } else {
        // At limit — show replace picker
        setSavedReceiptsForReplace(saved);
        setPhase('replace');
      }
    } catch {
      // Silent — not critical
    } finally {
      setSavingToPastScans(false);
    }
  };

  // ── Replace an old saved receipt with this one ──────────────────────────────
  const handleReplaceReceipt = async (oldReceiptId) => {
    if (savingToPastScans) return;
    setSavingToPastScans(true);
    try {
      await unsaveReceipt(oldReceiptId);
      const { success } = await saveReceiptToHistory(result.receiptId);
      if (success) {
        setPhase('done');
        setReceiptSaved(true);
      }
    } catch {
      // Silent
    } finally {
      setSavingToPastScans(false);
    }
  };

  // ── Moment 1 handlers ───────────────────────────────────────────────────────
  const handleScanPrompt = (answer) => {
    if (scanPromptDone) return;
    setScanAnswer(answer);
    if (answer === 'not_really') {
      // Hard negative — ask why before collapsing
      setScanFollowUp(true);
    } else {
      // Positive/neutral — save and auto-collapse
      localStorage.setItem('cms_seen_scan_prompt', '1');
      saveBetaFeedback('scan_quality', { answer }).catch(() => {});
      setTimeout(() => setScanPromptDone(true), 600);
    }
  };

  const handleScanFollowUpDone = () => {
    localStorage.setItem('cms_seen_scan_prompt', '1');
    saveBetaFeedback('scan_quality', { answer: scanAnswer, followUpText: scanFollowUpText || null }).catch(() => {});
    setScanPromptDone(true);
  };

  // ── Moment 2 handlers ───────────────────────────────────────────────────────
  const handleListClarity = (answer) => {
    setListClarityAns(answer);
    if (answer === 'confusing') {
      // Hard negative — ask why before advancing
      setListFollowUp(true);
    } else {
      setTimeout(() => setListStep(1), 400);
    }
  };

  const handleListFollowUpDone = () => {
    setListFollowUp(false);
    setTimeout(() => setListStep(1), 60);
  };

  const handleListValue = (answer) => {
    localStorage.setItem('cms_seen_list_prompt', '1');
    saveBetaFeedback('list_clarity', {
      clarity:         listClarityAns,
      clarityFollowUp: listFollowUpText || null,
      value:           answer,
    }).catch(() => {});
    setTimeout(() => setListStep(2), 400);
  };

  const dismissListPrompt = () => setListPromptDone(true);

  // ── Product loader (lazy) ────────────────────────────────────────────────────
  const loadProducts = async () => {
    if (allProducts) return allProducts;
    const snap = await db.collection('products').get();
    const prods = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(p => p.active !== false);
    setAllProducts(prods);
    return prods;
  };

  // ── Correction search handler ────────────────────────────────────────────────
  const handleCorrectionSearch = async (query) => {
    setCorrectionSearch(query);
    if (!query.trim()) { setCorrectionResults([]); return; }
    setCorrectionLoading(true);
    const prods = await loadProducts();
    const q = query.toLowerCase();
    const results = prods
      .filter(p => p.name?.toLowerCase().includes(q) || p.id.toLowerCase().includes(q))
      .slice(0, 8);
    setCorrectionResults(results);
    setCorrectionLoading(false);
  };

  // ── Save correction handler ──────────────────────────────────────────────────
  const handleSaveCorrection = async (item, correctProduct, saveAsAlias) => {
    setCorrectionSaving(true);
    try {
      if (saveAsAlias && user) {
        await saveAliasDirect(db, {
          receiptText:       item.productText,
          productId:         correctProduct.id,
          productName:       correctProduct.name,
          storeId:           result?.detectedStoreId || '',
          uid:               user.uid,
          originalProductId: item.matchedProductId || null,
        });
      }
      setCorrectingIdx(null);
      setCorrectionSearch('');
      setCorrectionResults([]);
    } catch (err) {
      console.error('handleSaveCorrection error', err);
    }
    setCorrectionSaving(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="receipt-backdrop" onClick={handleClose} />

      {/* Sheet */}
      <div className="receipt-sheet" role="dialog" aria-modal="true" aria-label="Scan your receipt">
        <div className="receipt-handle" aria-hidden="true" />

        {/* Header */}
        <div className="receipt-header">
          <span className="receipt-title">
            {phase === 'replace' ? 'Free up a saved slot' : 'Scan your receipt'}
          </span>
          <button className="receipt-close" onClick={handleClose} aria-label="Close">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="1" y1="1" x2="11" y2="11"/>
              <line x1="11" y1="1" x2="1" y2="11"/>
            </svg>
          </button>
        </div>

        <div className="receipt-body">

          {/* ── Idle ── */}
          {phase === 'idle' && (
            <>
              <div className="receipt-icon-wrap" aria-hidden="true">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="8" y="4" width="24" height="32" rx="3"/>
                  <line x1="14" y1="13" x2="26" y2="13"/>
                  <line x1="14" y1="18" x2="26" y2="18"/>
                  <line x1="14" y1="23" x2="22" y2="23"/>
                </svg>
              </div>
              <p className="receipt-prompt">
                Take or choose a photo of your supermarket receipt. We'll read the prices and check them against our data.
              </p>
              <p className="receipt-footnote">
                Your receipt is processed automatically and never stored permanently.
              </p>
              <button className="receipt-btn primary" onClick={triggerCamera}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 16V7a2 2 0 012-2h1.5L7 3h6l1.5 2H16a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2z"/>
                  <circle cx="10" cy="11" r="3"/>
                </svg>
                Take a photo
              </button>
              <button className="receipt-btn secondary" onClick={triggerLibrary}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="3" width="14" height="14" rx="2"/>
                  <circle cx="7.5" cy="7.5" r="1.5"/>
                  <path d="M3 13l4-4 3 3 2-2 5 5"/>
                </svg>
                Choose from library
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="receipt-file-input"
                onChange={handleFileChange}
                aria-hidden="true"
              />
              <input
                ref={libraryInputRef}
                type="file"
                accept="image/*"
                className="receipt-file-input"
                onChange={handleFileChange}
                aria-hidden="true"
              />
            </>
          )}

          {/* ── Uploading ── */}
          {phase === 'uploading' && (
            <>
              <div className="receipt-progress-wrap">
                <div className="receipt-progress-track">
                  <div className="receipt-progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <span className="receipt-progress-label">{progress}%</span>
              </div>
              <p className="receipt-status-text">Uploading…</p>
            </>
          )}

          {/* ── Processing ── */}
          {phase === 'processing' && (
            <>
              <div className="receipt-spinner" aria-label="Processing" />
              <p className="receipt-status-text">Reading your receipt…</p>
              {slowProcessing ? (
                <p className="receipt-footnote receipt-footnote--slow">
                  Taking a bit longer than usual — almost there…
                </p>
              ) : (
                <p className="receipt-footnote">This usually takes 5–15 seconds.</p>
              )}
            </>
          )}

          {/* ── Done ── */}
          {phase === 'done' && result && (() => {
            // Split matched items by whether they are catalogue-mapped or backlog-only.
            // Preserve original index so the admin correction panel still works.
            const allWithIdx     = result.matchedItems.map((item, idx) => ({ ...item, _origIdx: idx }));
            const catalogueItems = allWithIdx.filter(i => i.matchedProductId && i.matchedProductId !== '__backlog__');
            const recognisedItems = allWithIdx.filter(i => i.matchedProductId === '__backlog__');
            const unmatchedCount = result.itemCount - result.matchedItems.length;
            return (
              <>
                <div className="receipt-success-icon" aria-hidden="true">✓</div>
                <p className="receipt-success-heading">Receipt scanned</p>
                {!listSaved && (
                  <p className="receipt-success-sub">Got it — these are your items. Next step: turn this into a shopping list to compare prices.</p>
                )}

                <div className="receipt-match-summary">
                  {catalogueItems.length > 0 && (
                    <span className="receipt-match-row receipt-match-ok">
                      ✓ {catalogueItems.length} item{catalogueItems.length !== 1 ? 's' : ''} ready to compare
                    </span>
                  )}
                  {recognisedItems.length > 0 && (
                    <span className="receipt-match-row receipt-match-warn">
                      ⚠ {recognisedItems.length} recognised · not yet in our catalogue
                    </span>
                  )}
                  {unmatchedCount > 0 && (
                    result.detectedStoreId && !storeIsSupported(result.detectedStoreId) ? (
                      // Unsupported store — items were parsed OK, just no catalogue coverage yet.
                      // Don't frame this as a failure; the store notice below explains why.
                      <span className="receipt-match-row">
                        {unmatchedCount} item{unmatchedCount !== 1 ? 's' : ''} found on your receipt
                      </span>
                    ) : (
                      <span className="receipt-match-row receipt-match-warn">
                        ⚠ {unmatchedCount} item{unmatchedCount !== 1 ? 's' : ''} not in our catalogue
                      </span>
                    )
                  )}
                </div>

                {/* ── Catalogue-matched items (ready to compare) ── */}
                {catalogueItems.length > 0 && (
                  <div className="receipt-items-list">
                    <p className="receipt-items-section-label">MATCHED ITEMS</p>
                    {catalogueItems.map((item) => (
                      <div key={item._origIdx} className={`receipt-item-row${correctingIdx === item._origIdx ? ' receipt-item-row--correcting' : ''}`}>
                        <div className="receipt-item-main">
                          <span className="receipt-item-name">{item.matchedProductName}</span>
                          <span className="receipt-item-receipt-text">"{item.productText}"</span>
                        </div>
                        <div className="receipt-item-meta">
                          <span className={`receipt-item-conf receipt-item-conf--${item.confidence || 'low'}`}>
                            {item.matchType === 'alias' ? 'alias' : item.confidence || 'low'}
                          </span>
                          {isAdminUser(user) && correctingIdx !== item._origIdx && (
                            <button
                              className="receipt-item-wrong-btn"
                              onClick={() => {
                                setCorrectingIdx(item._origIdx);
                                setCorrectionSearch('');
                                setCorrectionResults([]);
                              }}
                            >
                              Wrong?
                            </button>
                          )}
                        </div>
                        {isAdminUser(user) && correctingIdx === item._origIdx && (
                          <ReceiptCorrectionPanel
                            item={item}
                            search={correctionSearch}
                            results={correctionResults}
                            loading={correctionLoading}
                            saving={correctionSaving}
                            onSearch={handleCorrectionSearch}
                            onSave={handleSaveCorrection}
                            onCancel={() => { setCorrectingIdx(null); setCorrectionSearch(''); setCorrectionResults([]); }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Recognised but not yet in catalogue ── */}
                {recognisedItems.length > 0 && (
                  <div className="receipt-items-list receipt-items-list--dim">
                    <p className="receipt-items-section-label">NOT YET IN OUR CATALOGUE</p>
                    {recognisedItems.map((item) => (
                      <div key={item._origIdx} className="receipt-item-row receipt-item-row--dim">
                        <div className="receipt-item-main">
                          <span className="receipt-item-name receipt-item-name--dim">{item.productText}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Moment 1 — scan quality micro-prompt ── */}
                {!scanPromptDone && result.matchedItems.length > 0 && (
                  <div className="receipt-micro-prompt">
                    <p className="receipt-micro-q">Did this match your receipt well?</p>
                    <div className="receipt-micro-opts">
                      {[
                        { value: 'yes_mostly',  label: '👍 Yes, mostly right' },
                        { value: 'some_issues', label: '😐 Some issues'       },
                        { value: 'not_really',  label: '👎 Not really'         },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          className={`receipt-micro-opt${scanAnswer === opt.value ? ' receipt-micro-opt--sel' : ''}`}
                          onClick={() => handleScanPrompt(opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {scanFollowUp && (
                      <div className="receipt-micro-followup">
                        <textarea
                          className="receipt-micro-followup-text"
                          placeholder="What didn't match?"
                          value={scanFollowUpText}
                          onChange={e => setScanFollowUpText(e.target.value)}
                          rows={2}
                          autoFocus
                        />
                        <button className="receipt-micro-followup-done" onClick={handleScanFollowUpDone}>
                          Done
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {result.detectedStoreId && !storeIsSupported(result.detectedStoreId) && (
                  <p className="receipt-store-notice">
                    We recognised this as a {getStoreName(result.detectedStoreId)} receipt.
                    We're still adding {getStoreName(result.detectedStoreId)} to price comparisons — your list is ready to use.
                  </p>
                )}

                {catalogueItems.length > 0 ? (
                  <div className="receipt-action-row">

                    {/* 1 — Save as shopping list */}
                    {listSaved ? (
                      <div className="receipt-list-saved-wrap">
                        <p className="receipt-list-saved-text">✓ Your shopping list is ready.</p>
                        <p className="receipt-list-saved-sub">This is what you'll come back to next time you shop.</p>
                        {savedListId && onNavigate && (
                          <button
                            className="receipt-view-list-btn"
                            onClick={() => { handleClose(); onNavigate('basket'); }}
                          >
                            View list →
                          </button>
                        )}

                        {/* ── Moment 2 — post-list micro-prompt (one-time, 2-step) ── */}
                        {/* Only ask about comparison quality for supported stores — unsupported stores have no price data to evaluate */}
                        {!listPromptDone && storeIsSupported(result?.detectedStoreId) && (
                          <div className="receipt-micro-prompt receipt-micro-prompt--list">

                            {/* Step 0 — clarity */}
                            {listStep === 0 && (
                              <>
                                <p className="receipt-micro-q">Does this comparison make sense?</p>
                                <div className="receipt-micro-opts">
                                  {[
                                    { value: 'yes_clear', label: '👍 Yes, clear' },
                                    { value: 'mostly',    label: '😐 Mostly'     },
                                    { value: 'confusing', label: '👎 Confusing'   },
                                  ].map(opt => (
                                    <button
                                      key={opt.value}
                                      className={`receipt-micro-opt${listClarityAns === opt.value ? ' receipt-micro-opt--sel' : ''}`}
                                      onClick={() => handleListClarity(opt.value)}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                                {listFollowUp && (
                                  <div className="receipt-micro-followup">
                                    <textarea
                                      className="receipt-micro-followup-text"
                                      placeholder="What felt confusing?"
                                      value={listFollowUpText}
                                      onChange={e => setListFollowUpText(e.target.value)}
                                      rows={2}
                                      autoFocus
                                    />
                                    <button className="receipt-micro-followup-done" onClick={handleListFollowUpDone}>
                                      Done
                                    </button>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Step 1 — value */}
                            {listStep === 1 && (
                              <>
                                <p className="receipt-micro-q">Would you use this again next time you shop?</p>
                                <div className="receipt-micro-opts">
                                  {[
                                    { value: 'yes',   label: 'Yes'   },
                                    { value: 'maybe', label: 'Maybe' },
                                    { value: 'no',    label: 'No'    },
                                  ].map(opt => (
                                    <button
                                      key={opt.value}
                                      className="receipt-micro-opt"
                                      onClick={() => handleListValue(opt.value)}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}

                            {/* Step 2 — CTA to full stepper */}
                            {listStep === 2 && (
                              <div className="receipt-micro-cta">
                                <p className="receipt-micro-q">Quick one — how did that feel overall?</p>
                                {onNavigate && (
                                  <button
                                    className="receipt-micro-fb-btn"
                                    onClick={() => { dismissListPrompt(); handleClose(); onNavigate('beta-feedback'); }}
                                  >
                                    Give quick feedback
                                  </button>
                                )}
                                <button className="receipt-micro-skip" onClick={dismissListPrompt}>
                                  Skip
                                </button>
                              </div>
                            )}

                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        className="receipt-btn primary"
                        disabled={listSaving}
                        onClick={async () => {
                          setListSaving(true);
                          try {
                            const id = await saveReceiptAsShoppingList({
                              receiptId:       result.receiptId,
                              matchedItems:    catalogueItems,  // only catalogue-mapped items go into the list
                              detectedStoreId: result.detectedStoreId,
                            });
                            setSavedListId(id);
                            setListSaved(true);
                          } catch (err) {
                            setErrorMsg(err.message || 'Could not save list. Please try again.');
                            setPhase('error');
                          } finally {
                            setListSaving(false);
                          }
                        }}
                      >
                        {listSaving ? 'Saving…' : 'Save as shopping list'}
                      </button>
                    )}

                    {/* 2 — Save to Past scans */}
                    {receiptSaved ? (
                      <p className="receipt-past-saved-note">✓ Saved to Past scans</p>
                    ) : (
                      <button
                        className="receipt-btn secondary"
                        disabled={savingToPastScans}
                        onClick={handleSaveToPastScans}
                      >
                        {savingToPastScans ? 'Saving…' : 'Save to Past scans'}
                      </button>
                    )}

                    {/* 3 — Done */}
                    <button className="receipt-btn tertiary" onClick={handleClose}>Done</button>

                  </div>
                ) : (
                  <>
                    <p className="receipt-success-sub">
                      We couldn't match any items to our catalogue yet — we'll keep improving!
                    </p>
                    {result?.receiptId && (
                      <div className="receipt-action-row">
                        {receiptSaved ? (
                          <p className="receipt-past-saved-note">✓ Saved to Past scans</p>
                        ) : (
                          <button
                            className="receipt-btn secondary"
                            disabled={savingToPastScans}
                            onClick={handleSaveToPastScans}
                          >
                            {savingToPastScans ? 'Saving…' : 'Save to Past scans'}
                          </button>
                        )}
                        <button className="receipt-btn tertiary" onClick={handleClose}>Done</button>
                      </div>
                    )}
                    {!result?.receiptId && (
                      <button className="receipt-btn primary" onClick={handleClose}>Done</button>
                    )}
                  </>
                )}
              </>
            );
          })()}

          {/* ── Replace ── */}
          {phase === 'replace' && (
            <>
              <p className="receipt-replace-prompt">
                You've saved {SAVED_RECEIPT_LIMIT} receipts. Choose one to remove from Past scans and save this one instead.
              </p>
              <div className="receipt-replace-list">
                {savedReceiptsForReplace.map(r => (
                  <button
                    key={r.id}
                    className="receipt-replace-item"
                    disabled={savingToPastScans}
                    onClick={() => handleReplaceReceipt(r.id)}
                  >
                    <span className="receipt-replace-store">
                      {getStoreName(r.detectedStoreId) || 'Unknown store'}
                    </span>
                    <span className="receipt-replace-meta">
                      {fmtShortDate(r.processedAt)}
                      {r.matchedCount > 0 && ` · ${r.matchedCount} matched`}
                    </span>
                  </button>
                ))}
              </div>
              <button
                className="receipt-btn tertiary"
                onClick={() => setPhase('done')}
                disabled={savingToPastScans}
              >
                Cancel
              </button>
            </>
          )}

          {/* ── Error ── */}
          {phase === 'error' && (
            <>
              <div className="receipt-error-icon" aria-hidden="true">!</div>
              <p className="receipt-error-heading">Something went wrong</p>
              <p className="receipt-error-msg">{errorMsg}</p>
              <button className="receipt-btn primary" onClick={() => setPhase('idle')}>Try again</button>
              <button className="receipt-btn secondary" onClick={handleClose}>Cancel</button>
            </>
          )}

        </div>
      </div>
    </>
  );
}

export default ReceiptUploadSheet;
